import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CasinoIcon from '@mui/icons-material/Casino';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupIcon from '@mui/icons-material/Group';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchPublicProfile,
  removeCompanion,
  sendCompanionRequest,
} from '../api/companions';
import TavernChatPanel from '../components/chat/TavernChatPanel';
import RemoveCompanionDialog from '../components/companions/RemoveCompanionDialog';
import GameCover from '../components/tavern/GameCover';
import PresenceBadge from '../components/companions/PresenceBadge';
import { getInitials } from '../components/companions/presence';
import { compactGameMetaLine, gameTitle } from '../components/games/gameFormat';
import { useToast } from '../components/tavern/ToastProvider';

const relationshipLabelKey = (friendship) => {
  if (!friendship || friendship.status === 'none') return 'publicProfile.relationship.none';
  if (friendship.status === 'accepted') return 'publicProfile.relationship.accepted';
  if (friendship.status === 'pending' && friendship.direction === 'incoming') {
    return 'publicProfile.relationship.incoming';
  }
  if (friendship.status === 'pending') return 'publicProfile.relationship.outgoing';
  return 'publicProfile.relationship.none';
};

const canRequestCompanion = (profile) => {
  const friendship = profile?.friendship;

  if (!profile || profile.isSelf) return false;
  return !friendship || friendship.status === 'none' || friendship.status === 'declined';
};

const canAcceptCompanion = (profile) =>
  profile?.friendship?.status === 'pending' && profile.friendship.direction === 'incoming';

const canRemoveCompanion = (profile) => profile?.friendship?.status === 'accepted';

const ProfileHero = ({ busy, onChat, onCompanionAction, onRemoveCompanion, profile, t }) => {
  const tags = profile.profileTags || [];
  const actionAllowed = canRequestCompanion(profile) || canAcceptCompanion(profile);
  const removeAllowed = canRemoveCompanion(profile);
  const chatAllowed = canRemoveCompanion(profile);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        p: { xs: 3.5, md: 5 },
        color: '#fff8e8',
        bgcolor: 'primary.dark',
        background:
          'radial-gradient(circle at 78% 18%, rgba(215,160,75,0.48), transparent 18rem), linear-gradient(145deg, #0F4C45, #062522)',
        boxShadow: '0 22px 54px -30px rgba(6, 37, 34, 0.8)',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: -8,
              borderRadius: '50%',
              bgcolor: 'secondary.main',
              filter: 'blur(12px)',
              opacity: 0.55,
            }}
          />
          <Avatar
            src={profile.avatarUrl || undefined}
            alt={profile.displayName}
            sx={{
              position: 'relative',
              width: { xs: 112, md: 132 },
              height: { xs: 112, md: 132 },
              border: '4px solid rgba(255,248,232,0.9)',
            }}
          >
            {getInitials(profile.displayName)}
          </Avatar>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: 'secondary.light', mb: 1 }}>
            {profile.tavernTitle || t('profile.defaults.tavernTitle')}
          </Typography>
          <Typography variant="h1">{t('publicProfile.title', { name: profile.displayName })}</Typography>
          {profile.motto ? (
            <Typography
              sx={{
                mt: 1,
                fontFamily: '"Cinzel", Georgia, serif',
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                fontStyle: 'italic',
                color: 'rgba(255,248,232,0.82)',
              }}
            >
              "{profile.motto}"
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
            <Chip
              label={t(relationshipLabelKey(profile.friendship))}
              color="secondary"
              sx={{ fontWeight: 800 }}
            />
            {tags.map((tag, index) => (
              <Chip key={tag} label={tag} color={index === 0 ? 'primary' : 'secondary'} />
            ))}
          </Stack>
        </Box>
        {actionAllowed || removeAllowed || chatAllowed ? (
          <Stack spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
            {actionAllowed ? (
              <Button
                variant="contained"
                color="secondary"
                startIcon={busy ? <CircularProgress color="inherit" size={18} /> : <PersonAddAltIcon />}
                disabled={busy}
                onClick={onCompanionAction}
              >
                {busy
                  ? t('publicProfile.actions.sending')
                  : t(canAcceptCompanion(profile) ? 'publicProfile.actions.accept' : 'publicProfile.actions.addCompanion')}
              </Button>
            ) : null}
            {removeAllowed ? (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ChatBubbleOutlineIcon />}
                disabled={busy}
                onClick={onChat}
              >
                {t('chat.actions.message')}
              </Button>
            ) : null}
            {removeAllowed ? (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PersonRemoveOutlinedIcon />}
                disabled={busy}
                onClick={onRemoveCompanion}
                sx={{
                  borderColor: 'rgba(255,248,232,0.48)',
                  color: 'rgba(255,248,232,0.9)',
                  '&:hover': {
                    borderColor: 'secondary.light',
                    bgcolor: 'rgba(255,248,232,0.08)',
                  },
                }}
              >
                {t('publicProfile.actions.removeCompanion')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

const StatsGrid = ({ profile, t }) => {
  const stats = [
    {
      label: t('publicProfile.stats.gamesKept'),
      value: profile.stats?.gamesKept ?? profile.gamesKept ?? 0,
      icon: Inventory2OutlinedIcon,
    },
    {
      label: t('publicProfile.stats.companions'),
      value: profile.stats?.companions ?? 0,
      icon: GroupIcon,
    },
    {
      label: t('publicProfile.stats.nightsHosted'),
      value: profile.stats?.nightsHosted ?? 0,
      icon: ShieldOutlinedIcon,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2,
      }}
    >
      {stats.map(({ icon: Icon, label, value }) => (
        <Card key={label} variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Icon color="secondary" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                {label}
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: '"Cinzel", Georgia, serif', fontSize: 32 }}>
              {value}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

const GameGrid = ({ emptySubtitle, emptyTitle, games, language, t }) => {
  if (!games.length) {
    return (
      <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
        <CardContent>
          <Typography variant="h4">{emptyTitle}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {emptySubtitle}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 2,
        alignItems: 'start',
      }}
    >
      {games.map((game, index) => (
        <Box key={game.id} sx={{ minWidth: 0 }}>
          <Box sx={{ position: 'relative' }}>
            <GameCover game={game} sx={{ width: '100%' }} />
            {index < 3 ? (
              <Chip
                label={`#${index + 1}`}
                color={index === 0 ? 'secondary' : 'primary'}
                sx={{ position: 'absolute', top: 10, left: 10, boxShadow: 1 }}
              />
            ) : null}
          </Box>
          <Typography
            variant="h4"
            sx={{
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {gameTitle(game, t, language)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {compactGameMetaLine(game, t)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const CompanionDiscoveryCard = ({
  busy,
  companion,
  onAdd,
  onOpen,
  t,
}) => {
  const canAdd = canRequestCompanion(companion);
  const canAccept = canAcceptCompanion(companion);

  return (
    <Card
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(companion)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(companion);
        }
      }}
      sx={{
        cursor: 'pointer',
        transition: 'border-color 160ms ease, transform 160ms ease',
        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-1px)' },
        '&:focus-visible': {
          outline: '3px solid',
          outlineColor: 'secondary.light',
          outlineOffset: 3,
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={companion.avatarUrl || undefined} alt={companion.displayName}>
              {getInitials(companion.displayName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" noWrap>
                {companion.displayName}
              </Typography>
              <PresenceBadge presence={companion.presence} />
            </Box>
            <ArrowForwardIcon color="primary" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Chip size="small" label={t(relationshipLabelKey(companion.friendship))} />
            {canAdd || canAccept ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  onAdd(companion);
                }}
              >
                {busy
                  ? t('publicProfile.actions.sending')
                  : t(canAccept ? 'publicProfile.actions.accept' : 'publicProfile.actions.add')}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const PublicProfilePage = () => {
  const { username = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { i18n, t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState('');
  const [busyUserId, setBusyUserId] = useState(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const language = i18n.resolvedLanguage || i18n.language;
  const profile = data?.profile || null;

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setErrorKey('');

    fetchPublicProfile(username)
      .then((payload) => {
        if (!mounted) return;
        if (payload.profile?.isSelf) {
          navigate('/profile', { replace: true });
          return;
        }
        setData(payload);
      })
      .catch((error) => {
        if (!mounted) return;
        setData(null);
        setErrorKey(error?.response?.status === 404
          ? 'publicProfile.errors.notFound'
          : 'publicProfile.errors.load');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [navigate, username]);

  const updateFriendship = (userId, friendship) => {
    setData((current) => {
      if (!current) return current;

      return {
        ...current,
        profile:
          current.profile?.id === userId
            ? { ...current.profile, friendship }
            : current.profile,
        companions: (current.companions || []).map((companion) =>
          companion.id === userId ? { ...companion, friendship } : companion
        ),
      };
    });
  };

  const markProfileCompanionRemoved = (userId) => {
    setData((current) => {
      if (!current) return current;

      const friendship = { id: null, status: 'none', direction: 'none' };

      return {
        ...current,
        profile:
          current.profile?.id === userId
            ? {
                ...current.profile,
                friendship,
                stats: {
                  ...(current.profile.stats || {}),
                  companions: Math.max(Number(current.profile.stats?.companions || 0) - 1, 0),
                },
              }
            : current.profile,
        companions: (current.companions || []).map((companion) =>
          companion.id === userId ? { ...companion, friendship } : companion
        ),
      };
    });
  };

  const handleCompanionAction = async (target) => {
    if (!target?.id || busyUserId) return;

    setBusyUserId(target.id);
    try {
      const response = await sendCompanionRequest(target.id);
      updateFriendship(target.id, {
        id: response.id || target.friendship?.id || null,
        status: response.status || 'pending',
        direction: response.status === 'accepted' ? 'none' : 'outgoing',
      });
      toast.success(
        response.status === 'accepted'
          ? t('publicProfile.feedback.accepted')
          : t('publicProfile.feedback.requested')
      );
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(t('publicProfile.feedback.alreadyPending'));
      } else {
        toast.error(t('publicProfile.feedback.requestError'));
      }
    } finally {
      setBusyUserId(null);
    }
  };
  const handleRemoveCompanion = async () => {
    if (!profile?.id || busyUserId) return;

    setBusyUserId(profile.id);
    try {
      await removeCompanion(profile.id);
      markProfileCompanionRemoved(profile.id);
      setRemoveDialogOpen(false);
      toast.success(t('companions.feedback.removed'));
    } catch (error) {
      toast.error(
        [404, 409].includes(Number(error?.response?.status))
          ? t('companions.feedback.removeAlreadyDone')
          : t('companions.feedback.removeError')
      );
      if ([404, 409].includes(Number(error?.response?.status))) {
        markProfileCompanionRemoved(profile.id);
        setRemoveDialogOpen(false);
      }
    } finally {
      setBusyUserId(null);
    }
  };

  if (loading) {
    return (
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="rounded" width={132} height={132} />
              <Skeleton variant="text" width="40%" height={34} />
              <Skeleton variant="text" width="70%" height={72} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (errorKey || !profile) {
    return (
      <Stack spacing={3}>
        <Alert severity="warning">{t(errorKey || 'publicProfile.errors.notFound')}</Alert>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography variant="h3">{t('publicProfile.unavailable.title')}</Typography>
              <Typography color="text.secondary">
                {t('publicProfile.unavailable.subtitle')}
              </Typography>
              <Button variant="contained" onClick={() => navigate('/friends')}>
                {t('publicProfile.actions.backToCompanions')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={5}>
      <ProfileHero
        busy={busyUserId === profile.id}
        onChat={() =>
          setChatContext({
            type: 'direct',
            userId: profile.id,
            title: t('chat.context.directTitle', { name: profile.displayName }),
            subtitle: t('chat.context.directSubtitle'),
            participants: [profile],
          })
        }
        onCompanionAction={() => handleCompanionAction(profile)}
        onRemoveCompanion={() => setRemoveDialogOpen(true)}
        profile={profile}
        t={t}
      />

      <StatsGrid profile={profile} t={t} />

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('publicProfile.sections.favoritesEyebrow')}
        </Typography>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('publicProfile.sections.favoritesTitle', { name: profile.displayName })}
        </Typography>
        <GameGrid
          emptyTitle={t('publicProfile.empty.favoritesTitle')}
          emptySubtitle={t('publicProfile.empty.favoritesSubtitle', { name: profile.displayName })}
          games={data.favoriteGames || []}
          language={language}
          t={t}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('publicProfile.sections.libraryEyebrow')}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <CasinoIcon color="secondary" />
          <Typography variant="h3">
            {t('publicProfile.sections.libraryTitle', { name: profile.displayName })}
          </Typography>
        </Stack>
        <GameGrid
          emptyTitle={t('publicProfile.empty.libraryTitle')}
          emptySubtitle={t('publicProfile.empty.librarySubtitle', { name: profile.displayName })}
          games={data.libraryPreview || []}
          language={language}
          t={t}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('publicProfile.sections.companionsEyebrow')}
        </Typography>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('publicProfile.sections.companionsTitle', { name: profile.displayName })}
        </Typography>
        {(data.companions || []).length === 0 ? (
          <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
            <CardContent>
              <Typography variant="h4">{t('publicProfile.empty.companionsTitle')}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                {t('publicProfile.empty.companionsSubtitle', { name: profile.displayName })}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {data.companions.map((companion) => (
              <CompanionDiscoveryCard
                key={companion.id}
                busy={busyUserId === companion.id}
                companion={companion}
                onAdd={handleCompanionAction}
                onOpen={(item) => navigate(`/u/${encodeURIComponent(item.username)}`)}
                t={t}
              />
            ))}
          </Box>
        )}
      </Box>
      <RemoveCompanionDialog
        companion={profile}
        loading={busyUserId === profile.id}
        open={removeDialogOpen}
        onClose={() => {
          if (busyUserId !== profile.id) setRemoveDialogOpen(false);
        }}
        onConfirm={handleRemoveCompanion}
      />
      <TavernChatPanel
        context={chatContext || {}}
        open={Boolean(chatContext)}
        onClose={() => setChatContext(null)}
      />
    </Stack>
  );
};

export default PublicProfilePage;
